/**
 * Cadastro rápido de cliente durante a venda na rota (POST /cadastros/pessoas/rapido): só o
 * necessário — nome, CPF/CNPJ ou telefone e, para a NF-e, endereço e situação no ICMS. Nada
 * administrativo (limite de crédito, tabela, vendedor): quem define é o servidor (o cliente
 * nasce com limite de crédito 0 — compra à vista até o escritório definir o limite).
 *
 * Abre por cima da venda (nada do que já foi digitado se perde) e, ao salvar, devolve o cliente
 * para ser selecionado. CPF/CNPJ já cadastrado: oferece selecionar o cliente existente.
 */
import { IE_INDICATORS, UF_LIST, onlyDigits, type Party } from "@cafegestor/shared";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Field, Icon, Input, Select } from "@cafegestor/ui";
import { ApiRequestError, apiFetch, errorMessage, newIdempotencyKey } from "@cafegestor/client";
import {
  EMPTY_CUSTOMER_FORM,
  quickCustomerBody,
  quickCustomerProblems,
  type CustomerForm,
} from "./customer-lib";

type Existing = { id: string; code: string; name: string };
type Form = CustomerForm;

export function QuickCustomerForm({
  initialName = "",
  onSaved,
  onSelectExisting,
  onCancel,
}: {
  /** o que foi digitado na pesquisa (vira o nome ou o documento) */
  initialName?: string;
  onSaved: (p: Party) => void;
  onSelectExisting: (id: string) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState<Form>(() => {
    const digits = onlyDigits(initialName);
    if (digits.length === 11 || digits.length === 14)
      return {
        ...EMPTY_CUSTOMER_FORM,
        kind: digits.length === 11 ? "pf" : "pj",
        document: initialName,
      };
    return { ...EMPTY_CUSTOMER_FORM, name: initialName };
  });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<Existing | null>(null);
  const [key] = useState(newIdempotencyKey);
  const nameRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => nameRef.current?.focus(), []);

  const problems = quickCustomerProblems(f);
  const show = (k: keyof Form) => (tried ? problems[k] : undefined);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const text = (k: keyof Form) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => set(k, e.target.value as Form[typeof k]),
    ...(show(k) ? { "aria-invalid": true } : {}),
  });

  async function save() {
    setTried(true);
    setError(null);
    setExisting(null);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    try {
      const p = await apiFetch<Party>("/cadastros/pessoas/rapido", {
        method: "POST",
        body: quickCustomerBody(f),
        idempotencyKey: key,
      });
      onSaved(p);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === "REGISTRO_DUPLICADO") {
        const d = err.details as { existing?: Existing } | undefined;
        if (d?.existing) setExisting(d.existing);
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="novo-cliente-titulo"
      className="fixed inset-0 z-50 flex flex-col bg-background sm:items-center sm:justify-center sm:bg-black/40 sm:p-4"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col bg-card sm:max-h-[90vh] sm:max-w-lg sm:flex-none sm:rounded-2xl sm:shadow-pop">
        <header className="flex items-center gap-3 border-b border-border/70 px-4 py-3">
          <span
            aria-hidden
            className="flex size-10 items-center justify-center rounded-full bg-caramel-soft text-accent"
          >
            <Icon name="userPlus" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="novo-cliente-titulo" className="text-[17px] font-semibold">
              Novo cliente
            </h2>
            <p className="text-[13px] text-muted-foreground">
              A venda continua aberta: nada do que você já digitou se perde.
            </p>
          </div>
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div role="radiogroup" aria-label="Tipo de cliente" className="grid grid-cols-2 gap-2">
            {(
              [
                ["pj", "Empresa (CNPJ)"],
                ["pf", "Pessoa física (CPF)"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={f.kind === k}
                onClick={() => set("kind", k)}
                className={`h-12 rounded-xl border-2 px-3 text-sm font-semibold ${
                  f.kind === k
                    ? "border-primary bg-caramel-soft text-primary"
                    : "border-input bg-card text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <Field
            label={f.kind === "pj" ? "Razão social" : "Nome completo"}
            required
            error={show("name")}
          >
            <Input ref={nameRef} autoComplete="off" {...text("name")} />
          </Field>
          {f.kind === "pj" && (
            <Field label="Nome fantasia">
              <Input autoComplete="off" {...text("tradeName")} />
            </Field>
          )}
          <Field
            label={f.kind === "pj" ? "CNPJ" : "CPF"}
            hint="Necessário para a NF-e. Sem ele, só Pedido de Venda."
            error={show("document")}
          >
            <Input inputMode="numeric" autoComplete="off" {...text("document")} />
          </Field>
          <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
            <Field label="Celular" error={show("phone")}>
              <Input inputMode="tel" autoComplete="off" {...text("mobile")} />
            </Field>
            <Field label="Telefone">
              <Input inputMode="tel" autoComplete="off" {...text("phone")} />
            </Field>
          </div>
          {f.kind === "pj" && (
            <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
              <Field label="Situação no ICMS">
                <Select
                  value={f.ieIndicator}
                  onChange={(e) => set("ieIndicator", e.target.value as Form["ieIndicator"])}
                >
                  {Object.entries(IE_INDICATORS).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              {f.ieIndicator === "1" && (
                <Field label="Inscrição estadual" required error={show("stateRegistration")}>
                  <Input inputMode="numeric" autoComplete="off" {...text("stateRegistration")} />
                </Field>
              )}
            </div>
          )}
          <fieldset className="min-w-0 space-y-4 rounded-xl border border-border/70 p-3">
            <legend className="px-1 text-sm font-semibold">Endereço (necessário para NF-e)</legend>
            <div className="grid grid-cols-[1fr_6rem] gap-3">
              <Field label="Rua">
                <Input autoComplete="off" {...text("street")} />
              </Field>
              <Field label="Número">
                <Input autoComplete="off" {...text("number")} />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
              <Field label="Bairro">
                <Input autoComplete="off" {...text("district")} />
              </Field>
              <Field label="CEP" error={show("cep")}>
                <Input inputMode="numeric" autoComplete="off" {...text("cep")} />
              </Field>
            </div>
            <div className="grid grid-cols-[1fr_5.5rem] gap-3">
              <Field label="Cidade">
                <Input autoComplete="off" {...text("city")} />
              </Field>
              <Field label="UF">
                <Select value={f.uf} onChange={(e) => set("uf", e.target.value)}>
                  <option value="">—</option>
                  {UF_LIST.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field
              label="Código IBGE do município"
              hint="7 dígitos (aparece na conta de luz ou na consulta do CEP)."
              error={show("cityIbgeCode")}
            >
              <Input inputMode="numeric" autoComplete="off" {...text("cityIbgeCode")} />
            </Field>
          </fieldset>
          <Field label="E-mail (para enviar a nota)">
            <Input type="email" inputMode="email" autoComplete="off" {...text("email")} />
          </Field>
          {tried && Object.keys(problems).length > 0 && (
            <Alert tone="error">Confira os campos marcados.</Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}
          {existing && (
            <Button className="w-full" onClick={() => onSelectExisting(existing.id)}>
              Selecionar {existing.code} — {existing.name}
            </Button>
          )}
        </div>
        <footer className="grid grid-cols-2 gap-2 border-t border-border/70 px-4 py-3">
          <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
            Voltar à venda
          </Button>
          <Button size="lg" onClick={() => void save()} disabled={busy}>
            {busy ? "Salvando..." : "Salvar cliente"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
